import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { 
  CreditCard, 
  CheckCircle, 
  Clock, 
  DollarSign,
  TrendingUp,
  BarChart3,
  RefreshCw,
  MessageSquare
} from 'lucide-react';
import { paymentAPI } from '../../services/api';
import { formatCurrency } from '../../utils/formatters';
import { useAdminTheme } from '../../contexts/AdminThemeContext';
import PaymentList from '../payment/PaymentList';
import PricingStatusModal from '../dashboard/PricingStatusModal';
import UnifiedEmailManagement from '../dashboard/UnifiedEmailManagement';
import UnavailableBookingsCard from '../dashboard/UnavailableBookingsCard';

const Dashboard = () => {
  const [searchParams] = useSearchParams();
  const paymentListRef = useRef(null);
  const { isDarkMode } = useAdminTheme();
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    paid: 0,
    failed: 0,
    expired: 0,
    totalPaid: 0,
    recentPayments: 0
  });
  const [isLoadingStats, setIsLoadingStats] = useState(true);
  const [isPricingModalOpen, setIsPricingModalOpen] = useState(false);
  
  // Cache management
  const [cache, setCache] = useState({
    stats: { data: null, timestamp: 0 }
  });
  const CACHE_DURATION = 30000; // 30 seconds cache

  const fetchStats = useCallback(async (forceRefresh = false) => {
    // Helper function to check if cache is valid
    const isCacheValid = (cacheKey) => {
      const now = Date.now();
      return cache[cacheKey]?.timestamp && (now - cache[cacheKey].timestamp) < CACHE_DURATION;
    };
    
    // Use cache if available and not forcing refresh
    if (!forceRefresh && isCacheValid('stats')) {
      setStats(cache.stats.data);
      setIsLoadingStats(false);
      return;
    }

    try {
      setIsLoadingStats(true);
      const response = await paymentAPI.getPaymentStats();
      if (response.success) {
        setStats(response.data);
        // Update cache
        setCache(prev => ({
          ...prev,
          stats: { data: response.data, timestamp: Date.now() }
        }));
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast.error('Failed to load statistics');
    } finally {
      setIsLoadingStats(false);
    }
  }, [cache]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const handleRefreshPayments = () => {
    if (paymentListRef.current) {
      paymentListRef.current.refresh();
    }
  };


  const statIconClass = isDarkMode ? 'text-accent-400' : 'text-accent-600';

  const statCards = [
    { title: 'Total Payments', value: stats.total, icon: CreditCard },
    { title: 'Pending', value: stats.pending, icon: Clock },
    { title: 'Paid', value: stats.paid, icon: CheckCircle },
    { title: 'Expired', value: stats.expired, icon: Clock },
    { title: 'Total Revenue', value: formatCurrency(stats.totalPaid), icon: DollarSign },
    { title: 'Recent (7 days)', value: stats.recentPayments, icon: TrendingUp }
  ];

  return (
    <div className="max-w-8xl mx-auto px-4 sm:px-5 lg:px-6 py-6 space-y-6 transition-colors duration-300">
      {/* Header */}
      <div>
        <h1 className={`text-3xl font-bold transition-colors duration-300 ${
          isDarkMode ? 'text-white' : 'text-gray-900'
        }`}>Dashboard</h1>
        <p className={`mt-2 transition-colors duration-300 ${
          isDarkMode ? 'text-skyone-muted-dark' : 'text-gray-600'
        }`}>
          Overview of your payment requests and transactions
        </p>
      </div>


      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div key={index} className={`rounded-lg shadow-sm border p-4 transition-colors duration-300 ${
              isDarkMode
                ? 'bg-skyone-card-dark border-skyone-border-dark'
                : 'bg-white border-gray-200'
            }`}>
              <div className="flex items-center">
                <Icon className={`w-5 h-5 flex-shrink-0 ${statIconClass}`} />
                <div className="ml-3 flex-1 min-w-0">
                  <p className={`text-xs font-medium truncate transition-colors duration-300 ${
                    isDarkMode ? 'text-skyone-muted-dark' : 'text-gray-600'
                  }`}>
                    {stat.title}
                  </p>
                  <div className="flex items-baseline">
                    {isLoadingStats ? (
                      <div className={`h-5 w-12 rounded animate-pulse transition-colors duration-300 ${
                        isDarkMode ? 'bg-skyone-inset-dark' : 'bg-gray-200'
                      }`}></div>
                    ) : (
                      <p className={`text-lg font-semibold truncate transition-colors duration-300 ${
                        isDarkMode ? 'text-white' : 'text-gray-900'
                      }`}>
                        {stat.value}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Quick Actions */}
      <div className={`rounded-lg shadow-sm border p-6 transition-colors duration-300 ${
        isDarkMode
          ? 'bg-skyone-card-dark border-skyone-border-dark'
          : 'bg-white border-gray-200'
      }`}>
        <div className="mb-6">
          <h2 className={`text-lg font-semibold transition-colors duration-300 ${
            isDarkMode ? 'text-white' : 'text-gray-900'
          }`}>Quick Actions</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Link
            to="/admin/create-payment"
            className={`flex items-center p-4 border rounded-lg transition-colors duration-200 ${
              isDarkMode
                ? 'border-skyone-border-dark hover:border-accent-500/50 hover:bg-skyone-inset-dark'
                : 'border-gray-200 hover:border-accent-400/60 hover:bg-lime-50'
            }`}
          >
            <CreditCard className={`w-6 h-6 flex-shrink-0 ${statIconClass}`} />
            <div className="ml-4">
              <h3 className={`text-sm font-medium transition-colors duration-300 ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>Create Payment</h3>
              <p className={`text-sm transition-colors duration-300 ${
                isDarkMode ? 'text-skyone-muted-dark' : 'text-gray-500'
              }`}>Create new payment request</p>
            </div>
          </Link>

          <Link
            to="/admin/payments?status=pending"
            className={`flex items-center p-4 border rounded-lg transition-colors duration-200 ${
              isDarkMode
                ? 'border-skyone-border-dark hover:border-accent-500/50 hover:bg-skyone-inset-dark'
                : 'border-gray-200 hover:border-accent-400/60 hover:bg-lime-50'
            }`}
          >
            <Clock className={`w-6 h-6 flex-shrink-0 ${statIconClass}`} />
            <div className="ml-4">
              <h3 className={`text-sm font-medium transition-colors duration-300 ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>Pending Payments</h3>
              <p className={`text-sm transition-colors duration-300 ${
                isDarkMode ? 'text-skyone-muted-dark' : 'text-gray-500'
              }`}>View pending requests</p>
            </div>
          </Link>

          <Link
            to="/admin/payments?status=paid"
            className={`flex items-center p-4 border rounded-lg transition-colors duration-200 ${
              isDarkMode
                ? 'border-skyone-border-dark hover:border-accent-500/50 hover:bg-skyone-inset-dark'
                : 'border-gray-200 hover:border-accent-400/60 hover:bg-lime-50'
            }`}
          >
            <CheckCircle className={`w-6 h-6 flex-shrink-0 ${statIconClass}`} />
            <div className="ml-4">
              <h3 className={`text-sm font-medium transition-colors duration-300 ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>Paid Payments</h3>
              <p className={`text-sm transition-colors duration-300 ${
                isDarkMode ? 'text-skyone-muted-dark' : 'text-gray-500'
              }`}>View completed payments</p>
            </div>
          </Link>

          <button
            onClick={() => setIsPricingModalOpen(true)}
            className={`flex items-center p-4 border rounded-lg transition-colors duration-200 text-left ${
              isDarkMode
                ? 'border-skyone-border-dark hover:border-accent-500/50 hover:bg-skyone-inset-dark'
                : 'border-gray-200 hover:border-accent-400/60 hover:bg-lime-50'
            }`}
          >
            <BarChart3 className={`w-6 h-6 flex-shrink-0 ${statIconClass}`} />
            <div className="ml-4">
              <h3 className={`text-sm font-medium transition-colors duration-300 ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>Pricing Status</h3>
              <p className={`text-sm transition-colors duration-300 ${
                isDarkMode ? 'text-skyone-muted-dark' : 'text-gray-500'
              }`}>View pricing information</p>
            </div>
          </button>
        </div>
      </div>

      {/* Email Management */}
      <UnifiedEmailManagement />

      {/* WhatsApp Management */}
      <div className={`mb-8 rounded-lg shadow-sm border transition-colors duration-300 ${
        isDarkMode
          ? 'bg-skyone-card-dark border-skyone-border-dark'
          : 'bg-white border-gray-200'
      }`}>
        {/* Header */}
        <div className={`px-6 py-4 border-b transition-colors duration-300 ${
          isDarkMode ? 'border-skyone-border-dark' : 'border-gray-200'
        }`}>
          <h2 className={`text-lg font-semibold transition-colors duration-300 ${
            isDarkMode ? 'text-white' : 'text-gray-900'
          }`}>WhatsApp Management</h2>
          <p className={`text-sm mt-1 transition-colors duration-300 ${
            isDarkMode ? 'text-skyone-muted-dark' : 'text-gray-600'
          }`}>
            Process WhatsApp messages and manage customer communications
          </p>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Link
              to="/admin/whatsapp"
              className={`flex items-center p-4 border rounded-lg transition-colors duration-200 ${
                isDarkMode
                  ? 'border-skyone-border-dark hover:border-accent-500/50 hover:bg-skyone-inset-dark'
                  : 'border-gray-200 hover:border-accent-400/60 hover:bg-lime-50'
              }`}
            >
              <MessageSquare className={`w-6 h-6 flex-shrink-0 ${statIconClass}`} />
              <div className="ml-4">
                <h3 className={`text-sm font-medium transition-colors duration-300 ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>WhatsApp Dashboard</h3>
                <p className={`text-sm transition-colors duration-300 ${
                  isDarkMode ? 'text-skyone-muted-dark' : 'text-gray-500'
                }`}>Manage WhatsApp messages and AI processing</p>
              </div>
            </Link>

            <Link
              to="/admin/whatsapp?tab=staging"
              className={`flex items-center p-4 border rounded-lg transition-colors duration-200 ${
                isDarkMode
                  ? 'border-skyone-border-dark hover:border-accent-500/50 hover:bg-skyone-inset-dark'
                  : 'border-gray-200 hover:border-accent-400/60 hover:bg-lime-50'
              }`}
            >
              <BarChart3 className={`w-6 h-6 flex-shrink-0 ${statIconClass}`} />
              <div className="ml-4">
                <h3 className={`text-sm font-medium transition-colors duration-300 ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>AI Processing</h3>
                <p className={`text-sm transition-colors duration-300 ${
                  isDarkMode ? 'text-skyone-muted-dark' : 'text-gray-500'
                }`}>Review AI-parsed message data</p>
              </div>
            </Link>

          </div>
        </div>
      </div>

      {/* Unavailable Bookings */}
      <UnavailableBookingsCard />

      {/* Recent Payments */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className={`text-lg font-semibold transition-colors duration-300 ${
            isDarkMode ? 'text-white' : 'text-gray-900'
          }`}>Recent Payments</h2>
          <button
            onClick={handleRefreshPayments}
            className="btn btn-secondary flex items-center"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </button>
        </div>
        <PaymentList 
          ref={paymentListRef}
          initialFilters={{
            status: searchParams.get('status') || 'all'
          }}
          hideHeader={true}
        />
      </div>

      {/* Pricing Status Modal */}
      <PricingStatusModal 
        isOpen={isPricingModalOpen}
        onClose={() => setIsPricingModalOpen(false)}
      />
    </div>
  );
};

export default Dashboard;
